import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * SpeechToText Component
 * 
 * A reusable component for converting speech to text using the Web Speech API.
 * This is a free, browser-native API that works without external services.
 * 
 * Supported browsers: Chrome, Edge, Safari (with webkit prefix)
 * 
 * @param {Function} onTranscript - Callback function that receives the transcribed text
 * @param {string} language - Language code (e.g., 'en-US', 'es-ES', 'fr-FR')
 * @param {boolean} continuous - Whether to continue listening after speech pauses
 * @param {boolean} interimResults - Whether to show interim results while speaking
 * @param {string} className - Additional CSS classes for the button
 */
const SpeechToText = ({ 
  onTranscript, 
  language = 'en-US', 
  continuous = true,
  interimResults = true,
  className = '' 
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check if browser supports Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      console.warn('Speech Recognition is not supported in this browser.');
      return;
    }

    // Initialize Speech Recognition
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    // Handle speech recognition results
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        onTranscript(finalTranscript.trim());
        setInterimText('');
      } else if (interimTranscript) {
        setInterimText(interimTranscript);
      }
    };

    // Handle errors
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      let errorMessage = 'Speech recognition error';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please check your device.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone permissions.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'aborted':
          // Don't show error for manual stop
          return;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }
      
      toast.error(errorMessage);
      setIsListening(false);
      setInterimText('');
    };

    // Handle recognition end
    recognition.onend = () => {
      setIsListening(false);
      setInterimText('');
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [language, continuous, interimResults, onTranscript]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isListening) {
      // Stop listening
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimText('');
    } else {
      // Start listening
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast.success('Listening... Speak now!');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Failed to start speech recognition. Please try again.');
      }
    }
  };

  if (!isSupported) {
    return (
      <button
        type="button"
        disabled
        className={`p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed ${className}`}
        title="Speech recognition not supported in this browser"
      >
        <MicOff className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggleListening}
        className={`
          relative p-2 rounded-md transition-all duration-200
          ${isListening 
            ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
            : 'bg-teal-500 hover:bg-teal-600 text-white'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        title={isListening ? 'Stop recording' : 'Start voice input'}
      >
        {isListening ? (
          <MicOff className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
      
      {interimText && (
        <span className="text-sm text-gray-500 dark:text-gray-400 italic">
          {interimText}
        </span>
      )}
    </div>
  );
};

export default SpeechToText;
